import { createContext, useContext, useMemo } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import {
  useGetAllUsers,
  useGetMyRecommendedUsers,
  useGetUsersCount,
  useSaveUser,
} from '@juno/client-api';
import { JunoUser, RoleEnum, UserCount, UserRecommendation } from '@juno/client-api/model';
import { FeatureConfig as FeatureConfigUtils } from '@juno/client-api/utils';
import { FEATURE_CONFIG_KEYS_ENUM } from '@juno/constants';
import {
  DefaultContextProps,
  FEATURE_FLAG,
  MutationAction,
  calculateUserSearchFilters,
  onMutation,
  useFeatureFlagWithUser,
  useQuerySearch,
  useSettings,
} from '@juno/utils';
import { DIRECTORY_DEFAULT_NUM_PER_PAGE } from '../Directory/DirectoryPagination';
import { ExtendedUser } from '../Directory/SavedUsers';
import { DirectoryDataModel } from '../Directory/form';
import { SAVED_PAGE_SIZE, useDirectoryFilters } from './DirectoryFiltersContext';

export interface DirectoryContextProps {
  handleSaveUser: (user: JunoUser) => void;
  settings: DefaultContextProps;
  component: DirectoryDataModel;
  showSavedUsers: boolean;
  showRecommendedUsers: boolean;
  numPerPage: number;
  filteredUsersQuery: UseQueryResult<JunoUser[], unknown>;
  filteredUsersCountQuery: UseQueryResult<UserCount, unknown>;
  allUsersCountQuery: UseQueryResult<UserCount, unknown>;
  recommendedUsersQuery: UseQueryResult<UserRecommendation[], unknown>;
  filteredSavedUsersQuery: UseQueryResult<JunoUser[], unknown>;
  filteredSavedUsersCountQuery: UseQueryResult<UserCount, unknown>;
  allSavedUsersCountQuery: UseQueryResult<UserCount, unknown>;
  loadingData: boolean;
  showMentorshipFilter: boolean;
  showTagsFilter: boolean;
}

export const DirectoryContext = createContext<DirectoryContextProps | undefined>(undefined);

export const DirectoryContextProvider = ({
  children,
  component,
}: {
  children: React.ReactNode;
  component: DirectoryDataModel;
}) => {
  const settings = useSettings();
  const user = settings.user as ExtendedUser;

  const showSavedUsers = component?.showSavedUsers === undefined ? true : component?.showSavedUsers;
  const showRecommendedUsers =
    component?.showRecommendedUsers === undefined ? true : component?.showRecommendedUsers;
  const numPerPage = component?.numPerPage || DIRECTORY_DEFAULT_NUM_PER_PAGE;

  // Component Prefilters
  const tagsPrefilter = useMemo(() => {
    const interests = component?.interestsFilter?.map((tag) => tag.id) || [];
    const skills = component?.skillsFilter?.map((tag) => tag.id) || [];
    if (interests.length === 0 && skills.length === 0) return undefined;
    return [...interests, ...skills];
  }, [component?.interestsFilter, component?.skillsFilter]);

  // Feature Flags
  const mentorshipEnabled = FeatureConfigUtils.getFeatureFlag(
    settings.configs,
    FEATURE_CONFIG_KEYS_ENUM.Mentorship,
  );

  const mentorshipPrefilter = useMemo(() => {
    if (!mentorshipEnabled) return undefined;
    const mentorshipFilter = component?.mentorshipFilter?.map((m) => m.id) || [];
    if (mentorshipFilter.length === 0) return undefined;
    return mentorshipFilter;
  }, [mentorshipEnabled, component?.mentorshipFilter]);

  const showMentorshipFilter = mentorshipEnabled
    ? component?.additionalFilterOptions?.some((option) => option.keyword === 'mentorship_status')
    : false;

  const { value: tagsFilterFeatureFlag, loading: tagsFilterFeatureFlagLoading } =
    useFeatureFlagWithUser(FEATURE_FLAG.DIRECTORY_TAG_FILTERS);
  const showTagsFilter = tagsFilterFeatureFlag && !tagsFilterFeatureFlagLoading;

  const { buildBackendFilters, sort, savedUsersPage } = useDirectoryFilters();
  const { backendSearch } = useQuerySearch({
    toBackend: calculateUserSearchFilters,
  });

  // Filtered Users Query
  const filteredUsersBackendFilters = useMemo(
    () =>
      buildBackendFilters({
        mergeWith: {
          filter_or: {
            mentorship_status__in: mentorshipPrefilter,
            ...backendSearch?.filter_or,
          },
          filter: {
            active: true,
            ...backendSearch?.filter,
            declared_tags__in: tagsPrefilter,
          },
          exclude_or: { role: RoleEnum.NUMBER_2 },
          filter_and_or: {
            ...backendSearch?.filter_and_or,
          },
        },
      }),
    [buildBackendFilters, mentorshipPrefilter, backendSearch, tagsPrefilter],
  );

  const filteredUsersQuery = useGetAllUsers(
    settings.platform?.id || '',
    {
      ...filteredUsersBackendFilters,
      limit: component?.numPerPage || DIRECTORY_DEFAULT_NUM_PER_PAGE,
      include: 'settings',
    },
    {
      query: { enabled: !!settings.platform?.id && !!component },
    },
  );

  const filteredUsersCountQuery = useGetUsersCount(
    settings.platform?.id || '',
    {
      ...filteredUsersBackendFilters,
    },
    {
      query: { enabled: !!settings.platform?.id && !!component },
    },
  );

  const allUsersCountQuery = useGetUsersCount(
    settings.platform?.id || '',
    {
      filter: { active: true, declared_tags__in: tagsPrefilter },
      exclude_or: { role: RoleEnum.NUMBER_2 },
      filter_or: { mentorship_status__in: mentorshipPrefilter },
    },
    {
      query: { enabled: !!settings.platform?.id && !!component },
    },
  );

  // Recommended Users Query
  const recommendedUsersQuery = useGetMyRecommendedUsers(
    settings.platform?.id || '',
    { include: 'settings' },
    { query: { enabled: !!settings.platform?.id && showRecommendedUsers } },
  );

  // Saved Users Query
  const savedUsersBackendFilters = useMemo(
    () =>
      buildBackendFilters({
        mergeWith: {
          filter: {
            id__in: user?.saved_users,
            active: true,
            ...backendSearch?.filter,
            declared_tags__in: tagsPrefilter,
          },
          filter_or: {
            mentorship_status__in: mentorshipPrefilter,
            ...backendSearch?.filter_or,
          },
          exclude_or: { role: RoleEnum.NUMBER_2 },
          filter_and_or: {
            ...backendSearch?.filter_and_or,
          },
        },
        omit: { page: true },
      }),
    [buildBackendFilters, user?.saved_users, backendSearch, tagsPrefilter, mentorshipPrefilter],
  );

  const filteredSavedUsersQuery = useGetAllUsers(
    settings.platform?.id || '',
    {
      ...savedUsersBackendFilters,
      order: sort,
      limit: SAVED_PAGE_SIZE,
      offset: savedUsersPage - 1,
      include: 'settings',
    },
    {
      query: {
        enabled: !!settings.site?.id && showSavedUsers,
        placeholderData: (users) => users, // To prevent UI from flickering
      },
    },
  );

  const filteredSavedUsersCountQuery = useGetUsersCount(
    settings.platform?.id || '',
    {
      ...savedUsersBackendFilters,
    },
    { query: { enabled: !!settings.site?.id && showSavedUsers } },
  );

  const allSavedUsersCountQuery = useGetUsersCount(
    settings.platform?.id || '',
    {
      filter: { id__in: user?.saved_users || [], active: true },
    },
    { query: { enabled: !!settings.site?.id && showSavedUsers } },
  );

  const refetchAfterSaveUser = () => {
    filteredSavedUsersQuery.refetch();
    settings.refetchMe?.();
  };

  const saveUserMutation = useSaveUser(onMutation(MutationAction.CREATE, '', refetchAfterSaveUser));

  const handleSaveUser = async (user: JunoUser) => {
    if (filteredSavedUsersQuery.isLoading) return;
    try {
      await saveUserMutation.mutateAsync({ siteId: settings.site?.id || '', userId: user.id });
    } catch (e) {
      console.error(e);
    }
  };

  const loadingData =
    filteredUsersQuery.isLoading ||
    filteredSavedUsersQuery.isLoading ||
    recommendedUsersQuery.isLoading;

  return (
    <DirectoryContext.Provider
      value={{
        handleSaveUser,
        settings,
        component,
        showSavedUsers,
        showRecommendedUsers,
        numPerPage,
        filteredUsersQuery,
        filteredUsersCountQuery,
        allUsersCountQuery,
        recommendedUsersQuery,
        filteredSavedUsersQuery,
        filteredSavedUsersCountQuery,
        allSavedUsersCountQuery,
        loadingData,
        showMentorshipFilter,
        showTagsFilter,
      }}
    >
      {children}
    </DirectoryContext.Provider>
  );
};

export const useDirectoryData = () => {
  const context = useContext(DirectoryContext);

  if (!context) {
    throw new Error('useDirectoryData must be used within a DirectoryContextProvider');
  }

  return context;
};
